import { INSTAGRAM_POST_TAG_PRODUCT_TYPE } from "@apps/instagram/constants";
import InstagramPostMediaEntity from "@apps/instagram/entities/InstagramPostMediaEntity";
import InstagramPostProductTagEntity from "@apps/instagram/entities/InstagramPostProductTagEntity";
import InstagramPostTagEntity from "@apps/instagram/entities/InstagramPostTagEntity";
import InstagramPostsQuery from "@apps/instagram/queries/InstagramPostsQuery";
import InstagramTaggedProductServiceInterface from "@apps/instagram/services/InstagramTaggedProductService/InstagramTaggedProductServiceInteface";
import InstagramPostsStore from "@apps/instagram/stores/InstagramPostsStore";
import { ProductsServiceInterface } from "@modules/products/services/ProductsServiceInterface";
import { BehaviorSubject } from "rxjs";
import { filter, map, switchMap } from "rxjs/operators";

import { getNextElementInArray, getPreviousElementInArray } from "./utils";

const isProductTag = (
    tag: InstagramPostTagEntity
): tag is InstagramPostProductTagEntity =>
    tag.type === INSTAGRAM_POST_TAG_PRODUCT_TYPE;

class InstagramPostModalManager {
    activePost$ = this.postsQuery.active$;
    activeMediaId$ = new BehaviorSubject<number | null>(null);
    activeMedia$ = new BehaviorSubject<InstagramPostMediaEntity | null>(null);
    activeMediaTags$ = new BehaviorSubject<InstagramPostTagEntity[]>([]);
    activeMediaProductTags$ = new BehaviorSubject<
        InstagramPostProductTagEntity[]
    >([]);
    selectedTagId$ = new BehaviorSubject<number | null>(null);
    // this is id of product, that we opened
    openedProductTagId$ = new BehaviorSubject<number | null>(null);
    openedProductTag$ =
        new BehaviorSubject<InstagramPostProductTagEntity | null>(null);
    openedProductTags$ = new BehaviorSubject<
        InstagramPostProductTagEntity[] | null
    >(null);
    constructor(
        private readonly postsQuery: InstagramPostsQuery,
        private readonly postsStore: InstagramPostsStore,
        private readonly taggedProductService: InstagramTaggedProductServiceInterface,
        private readonly productsService: ProductsServiceInterface
    ) {
        // get all siblinged product tags
        const productTags$ = this.openedProductTagId$.pipe(
            map(() => {
                return (
                    this.postsQuery.active$
                        .getValue()
                        ?.tags.filter(isProductTag) || null
                );
            })
        );
        productTags$.subscribe(this.openedProductTags$);
        // get currently opened productTag
        productTags$
            .pipe(
                map((v) => {
                    return (
                        v?.find(
                            (t) => t.id === this.openedProductTagId$.getValue()
                        ) || null
                    );
                })
            )
            .subscribe(this.openedProductTag$);
        // это код для того что бы activeMediaId$ сразу при появлении активного поста по дефолту брал id первой фотки
        this.activePost$.subscribe((instagramPost) => {
            instagramPost?.tags.map((productTag) => {
                if (productTag.type === "product") {
                    productTag.product.id;
                }
            });
            this.activeMediaId$.next(instagramPost?.media[0].id || null);
        });
        this.activeMediaId$
            .pipe(
                map((mediaId) => {
                    const activePost = this.activePost$.getValue();
                    return (
                        activePost?.media.find(
                            (media) => media.id === mediaId
                        ) || null
                    );
                })
            )
            .subscribe(this.activeMedia$);
        this.activeMediaId$
            .pipe(
                map((mediaId) => {
                    const activePost = this.activePost$.getValue();
                    return (
                        activePost?.tags.filter(
                            (tag) => tag.mediaId === mediaId
                        ) || []
                    );
                })
            )
            .subscribe(this.activeMediaTags$);
        this.activeMediaId$
            .pipe(
                map((mediaId) => {
                    const activePost = this.activePost$.getValue();
                    return activePost?.tags.filter(
                        (tag) =>
                            tag.mediaId === mediaId &&
                            tag.type === INSTAGRAM_POST_TAG_PRODUCT_TYPE
                    ) as InstagramPostProductTagEntity[];
                })
            )
            .subscribe(this.activeMediaProductTags$);
        this.openedProductTag$
            .pipe(
                filter((v): v is InstagramPostProductTagEntity => !!v),
                switchMap((tag) => {
                    this.productsService.addProductForFetching(tag.product.id);
                    return this.taggedProductService.getTaggedProductByProductId(
                        tag.product.id
                    );
                })
            )
            .subscribe();
    }
    changeActivePost(id: number): void {
        if (!this.postsQuery.hasEntity(id)) {
            throw new Error(`Post with id ${id} does not exists in store`);
        }
        this.postsStore.setActive(id);
    }
    changeMediaId(id: number | null) {
        this.activeMediaId$.next(id);
    }
    /**
     * Go to the next post in list
     */
    next(): void {
        this.postsStore.setActive({
            next: true,
        });
    }
    /**
     * Go to the previous post in list
     */
    prev(): void {
        this.postsStore.setActive({
            prev: true,
        });
    }
    /**
     * Select some tag in post (for hightlighting)
     * @param id
     */
    selectTag(tagId: number): void {
        this.selectedTagId$.next(tagId);
    }
    /**
     * unSelect some tag in post (for hightlighting)
     *
     */
    unSelectTag(): void {
        this.selectedTagId$.next(null);
    }
    /**
     * Select product (this action will change active view in modal)
     * @param productTagId
     */
    openProductTag(productTagId: number | null): void {
        this.openedProductTagId$.next(productTagId);
    }
    /**
     * Go to the next product tag (for navigation in product view)
     */
    nextProductTag(): void {
        const openedProductTags = this.openedProductTags$.getValue();
        if (!openedProductTags) throw new Error();
        const currentProductTag = this.openedProductTag$.getValue();
        const nextProductTag = getNextElementInArray(
            currentProductTag,
            openedProductTags
        );
        if (!nextProductTag) throw new Error();
        this.openedProductTagId$.next(nextProductTag.id);
    }
    /**
     * Go to the next product tag (for navigation in product view)
     */
    prevProductTag(): void {
        const openedProductTags = this.openedProductTags$.getValue();
        if (!openedProductTags) throw new Error();
        const currentProductTag = this.openedProductTag$.getValue();
        const nextProductTag = getPreviousElementInArray(
            currentProductTag,
            openedProductTags
        );
        if (!nextProductTag) throw new Error();
        this.openedProductTagId$.next(nextProductTag.id);
    }
}

export default InstagramPostModalManager;
